import { useMutation } from "@apollo/client";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { convertStateNameToAbbreviation } from "encamp-shared/src/utils/states";
import { gql } from "generated-graphql";
import { WorkflowManagerConfigType } from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";
import { sortBy } from "lodash";
import { WORKFLOW_MANAGER_CONFIG_RULES_QUERY } from "./Rules";

const CREATE_WORKFLOW_MANAGER_CONFIG_RULE_MUTATION = gql(`
  mutation CreateWorkflowManagerConfigRule($type: WorkflowManagerConfigType!, $value: String!, $notes: String) {
    createWorkflowManagerConfigRule(type: $type, value: $value, notes: $notes)
  }
`);

type RuleType = {
  action: "allow" | "deny";
  entity:
    | "state"
    | "stateRevision"
    | "facility"
    | "facilityRevision"
    | "tenant";
  label: string;
  configType: WorkflowManagerConfigType;
};

interface AddRuleModalProps {
  open: boolean;
  onClose: () => void;
  onRuleAdded: () => void;
}

// Combined rule options grouped by Allow first, then Deny
const RULE_TYPE_OPTIONS: RuleType[] = [
  // Allow options
  {
    action: "allow",
    entity: "state",
    label: "Allow - State",
    configType: WorkflowManagerConfigType.AllowState,
  },
  {
    action: "allow",
    entity: "stateRevision",
    label: "Allow - State Revision",
    configType: WorkflowManagerConfigType.AllowStateRevision,
  },
  {
    action: "allow",
    entity: "facility",
    label: "Allow - Facility",
    configType: WorkflowManagerConfigType.AllowFacility,
  },
  {
    action: "allow",
    entity: "facilityRevision",
    label: "Allow - Facility Revision",
    configType: WorkflowManagerConfigType.AllowFacilityRevision,
  },
  // Deny options
  {
    action: "deny",
    entity: "facility",
    label: "Deny - Facility",
    configType: WorkflowManagerConfigType.DenyFacility,
  },
  {
    action: "deny",
    entity: "facilityRevision",
    label: "Deny - Facility Revision",
    configType: WorkflowManagerConfigType.DenyFacilityRevision,
  },
  {
    action: "deny",
    entity: "tenant",
    label: "Deny - Tenant",
    configType: WorkflowManagerConfigType.DenyTenant,
  },
];

export function AddRuleModal({
  open,
  onClose,
  onRuleAdded,
}: AddRuleModalProps) {
  const alerts = useAlerts();
  const [selectedRuleType, setSelectedRuleType] = useState<RuleType | null>(
    null
  );
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [stateInputValue, setStateInputValue] = useState("");
  const [needsCandidate, setNeedsCandidate] = useState(false);
  const [notImplemented, setNotImplemented] = useState(false);

  const [createMutation, { loading }] = useMutation(
    CREATE_WORKFLOW_MANAGER_CONFIG_RULE_MUTATION,
    {
      optimisticResponse: {
        createWorkflowManagerConfigRule: true,
      },
      update: (cache, result, options) => {
        // Optimistically update the cache by reading current data and adding a temporary rule
        // The refetch will replace this with the real data
        // Use the transformed value from mutation variables to ensure consistency
        try {
          const existingData = cache.readQuery({
            query: WORKFLOW_MANAGER_CONFIG_RULES_QUERY,
          });

          if (existingData && options.variables) {
            // Get the transformed value from mutation variables (same as what's sent to server)
            const transformedValue = options.variables.value as string;

            // Create a temporary optimistic rule
            const tempId = `temp-${Date.now()}`;
            const tempRule = {
              __typename: "WorkflowManagerConfigRule" as const,
              id: tempId,
              type: options.variables.type as WorkflowManagerConfigType,
              value: transformedValue,
              humanReadableValue: transformedValue, // Will be replaced by refetch
              notes: (options.variables.notes as string | null) || null,
              createdAt: new Date().toISOString(),
              createdBy: "current user", // Will be replaced by refetch
              updatedAt: new Date().toISOString(),
              updatedBy: "current user", // Will be replaced by refetch
              facility: null,
              tenant: null,
            };

            cache.writeQuery({
              query: WORKFLOW_MANAGER_CONFIG_RULES_QUERY,
              data: {
                ...existingData,
                workflowManagerConfigRules: {
                  ...existingData.workflowManagerConfigRules,
                  items: [
                    tempRule,
                    ...existingData.workflowManagerConfigRules.items,
                  ],
                  count: existingData.workflowManagerConfigRules.count + 1,
                },
              },
            });
          }
        } catch (error) {
          // If cache read fails, that's okay - refetch will handle it
          console.warn("Failed to optimistically update cache:", error);
        }
      },
      refetchQueries: [WORKFLOW_MANAGER_CONFIG_RULES_QUERY],
      onCompleted: () => {
        alerts.success("Rule added successfully");
        handleClose();
        onRuleAdded();
      },
      onError: (error) => {
        alerts.error(`Failed to add rule: ${error.message}`);
      },
    }
  );

  const handleClose = useCallback(() => {
    setSelectedRuleType(null);
    setValue("");
    setNotes("");
    setStateInputValue("");
    setNeedsCandidate(false);
    setNotImplemented(false);
    onClose();
  }, [onClose]);

  // Normalize state input: extract abbreviation from various formats
  // Always returns just the 2-letter state code (e.g., "AK")
  // This handles free text input (when users type instead of selecting from dropdown)
  const extractStateCode = useCallback((input: string): string => {
    const trimmed = input.trim();

    // Check if it's already a 2-letter code (case-insensitive)
    const upperTrimmed = trimmed.toUpperCase();
    if (/^[A-Z]{2}$/.test(upperTrimmed)) {
      return upperTrimmed;
    }

    // Try to extract from format "State Name (ABBR)" or "(ABBR)"
    const parenMatch = trimmed.match(/\(([A-Z]{2})\)/i);
    if (parenMatch) {
      return parenMatch[1].toUpperCase();
    }

    // Try to convert state name to abbreviation (handles "Alaska" or "ALASKA" -> "AK")
    const abbreviation = convertStateNameToAbbreviation(trimmed);
    if (abbreviation) {
      return abbreviation;
    }

    // Return as-is (uppercased) for custom values or unrecognized input
    return upperTrimmed;
  }, []);

  // Transform the input value to the final value that will be sent to the server
  // This ensures the same transformed value is used in both the cache and mutation
  const computeFinalValue = useCallback(
    (
      inputValue: string,
      ruleType: RuleType,
      needsCandidateFlag: boolean,
      notImplementedFlag: boolean
    ): string => {
      const isStateType =
        ruleType.entity === "state" || ruleType.entity === "stateRevision";

      if (isStateType) {
        // Extract state code from input (handles "AK", "Alaska", "Alaska (AK)", etc.)
        const stateCode = extractStateCode(inputValue.trim());

        // Format value for state revision rules (using old screaming snake case format)
        if (
          ruleType.entity === "stateRevision" &&
          ruleType.action === "allow"
        ) {
          if (needsCandidateFlag) {
            return `${stateCode}_NEEDS_CANDIDATE`;
          } else if (notImplementedFlag) {
            return `${stateCode}_NOT_IMPLEMENTED`;
          } else {
            return stateCode;
          }
        } else {
          return stateCode;
        }
      } else {
        return inputValue.trim();
      }
    },
    [extractStateCode]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedRuleType) {
      alerts.error("Please select a rule type");
      return;
    }

    if (!value || value.trim().length === 0) {
      alerts.error("Please enter a value");
      return;
    }

    try {
      const finalValue = computeFinalValue(
        value,
        selectedRuleType,
        needsCandidate,
        notImplemented
      );

      createMutation({
        variables: {
          type: selectedRuleType.configType,
          value: finalValue,
          notes: notes.trim() || null,
        },
      });
    } catch (error) {
      alerts.error(
        error instanceof Error ? error.message : "Invalid rule configuration"
      );
    }
  }, [
    selectedRuleType,
    value,
    notes,
    needsCandidate,
    notImplemented,
    computeFinalValue,
    createMutation,
    alerts,
  ]);

  const stateOptions = useMemo(
    () =>
      sortBy(Object.entries(STATE_NAMES), 1).map(([abbr, name]) => ({
        value: abbr,
        // Ensure title case for state name, uppercase for abbreviation
        label: `${name} (${abbr.toUpperCase()})`,
      })),
    []
  );

  const isStateType =
    selectedRuleType?.entity === "state" ||
    selectedRuleType?.entity === "stateRevision";
  const isUuidType =
    selectedRuleType?.entity === "facility" ||
    selectedRuleType?.entity === "facilityRevision" ||
    selectedRuleType?.entity === "tenant";

  // Reset value when rule type changes
  const handleRuleTypeChange = useCallback((newRuleType: RuleType | null) => {
    setSelectedRuleType(newRuleType);
    setValue("");
    setStateInputValue("");
    setNeedsCandidate(false);
    setNotImplemented(false);
  }, []);

  const isStateRevisionAllow =
    selectedRuleType?.action === "allow" &&
    selectedRuleType?.entity === "stateRevision";

  const handleNeedsCandidateChange = useCallback((checked: boolean) => {
    setNeedsCandidate(checked);
    if (checked) {
      setNotImplemented(false);
    }
  }, []);

  const handleNotImplementedChange = useCallback((checked: boolean) => {
    setNotImplemented(checked);
    if (checked) {
      setNeedsCandidate(false);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Add Rule"
      maxWidth="sm"
      fullWidth
      hideCancelButton
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button key="cancel" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="add"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !selectedRuleType || !value}
        >
          {loading ? "Adding..." : "Add Rule"}
        </Button>,
      ]}
    >
      <Stack spacing={3}>
        <FormControl fullWidth>
          <Autocomplete
            options={RULE_TYPE_OPTIONS}
            value={selectedRuleType}
            onChange={(_, newValue) => {
              handleRuleTypeChange(newValue);
            }}
            groupBy={(option) => {
              // Group by action (Allow/Deny)
              return option.action === "allow" ? "Allow" : "Deny";
            }}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rule Type"
                placeholder="Select rule type"
              />
            )}
          />
        </FormControl>

        {isStateType && (
          <FormControl fullWidth>
            <Autocomplete
              freeSolo
              options={stateOptions}
              value={
                value
                  ? stateOptions.find((opt) => opt.value === value) || value
                  : null
              }
              inputValue={stateInputValue}
              onInputChange={(_, newInputValue) => {
                setStateInputValue(newInputValue);
                setValue(newInputValue);
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  setValue(newValue);
                  setStateInputValue(newValue);
                } else if (
                  newValue &&
                  typeof newValue === "object" &&
                  "value" in newValue
                ) {
                  setValue(newValue.value);
                  setStateInputValue(newValue.value);
                } else {
                  setValue("");
                  setStateInputValue("");
                }
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                if (typeof option === "object" && "value" in option) {
                  return option.label || option.value;
                }
                return "";
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  placeholder="Enter state code (e.g., CA) or select from list"
                  helperText="You can enter any state code, including custom values"
                />
              )}
            />
            {isStateRevisionAllow && (
              <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={needsCandidate}
                      onChange={(e) =>
                        handleNeedsCandidateChange(e.target.checked)
                      }
                    />
                  }
                  label="Needs Candidate"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notImplemented}
                      onChange={(e) =>
                        handleNotImplementedChange(e.target.checked)
                      }
                    />
                  }
                  label="Not Implemented"
                />
              </Stack>
            )}
          </FormControl>
        )}

        {isUuidType && (
          <TextField
            fullWidth
            label={
              selectedRuleType?.entity === "tenant"
                ? "Tenant UUID"
                : "Facility UUID"
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter UUID"
            helperText={`Enter the ${
              selectedRuleType?.entity === "tenant" ? "tenant" : "facility"
            } UUID`}
          />
        )}

        <TextField
          fullWidth
          label="Notes (Optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          placeholder="Add any notes about this rule"
        />
      </Stack>
    </Dialog>
  );
}
